"use client"

import Link from "next/link"
import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { BackgroundGrid } from "@/components/ui/background-grid"
import { SubtleSpotlight } from "@/components/ui/subtle-spotlight"
import { ArrowRight, Sparkles } from "lucide-react"

const signupNotes = [
  {
    title: "Layered surface",
    detail: "Frosted card with elevated edges keeps attention on the fields.",
  },
  {
    title: "Calm motion",
    detail: "Spotlights and hover cues hint at focus changes without noise.",
  },
  {
    title: "Onboarding signal",
    detail: "Only email and password so the funnel feels immediate.",
  },
]

function SignUpForm() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [msg, setMsg] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setMsg(null)
    setLoading(true)

    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      })
      const json = await res.json()
      if (!res.ok) {
        setMsg(json.error || "Sign up failed")
      } else {
        window.location.href = "/signin"
      }
    } catch (error) {
      setMsg("Something went wrong")
    } finally {
      setLoading(false)
    }
  }

  return (
    <SubtleSpotlight className="w-full" color="rgba(59,130,246,0.18)">
      <Card className="relative overflow-hidden rounded-[26px] border border-white/10 bg-slate-950/70 shadow-[0_35px_120px_-60px_rgba(14,165,233,1)] backdrop-blur">
        <div className="pointer-events-none absolute -inset-12 bg-gradient-to-br from-sky-500/10 via-transparent to-transparent" />
        <CardHeader className="relative space-y-4 pb-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.35em] text-slate-100/80">
            <Sparkles className="h-3.5 w-3.5 text-sky-300" />
            New workspace
          </div>
          <CardTitle className="text-3xl font-semibold text-white">Sign up</CardTitle>
          <CardDescription className="text-sm text-slate-300">
            Just email and password on a layered surface with steady lighting cues.
          </CardDescription>
        </CardHeader>
        <CardContent className="relative space-y-6">
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[0.7rem] uppercase tracking-[0.35em] text-slate-300">
                Work email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@workflow.dev"
                autoComplete="email"
                className="h-12 rounded-2xl border-white/15 bg-transparent text-sm text-white placeholder:text-slate-500 focus-visible:border-sky-400/70 focus-visible:ring-0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[0.7rem] uppercase tracking-[0.35em] text-slate-300">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                className="h-12 rounded-2xl border-white/15 bg-transparent text-sm text-white placeholder:text-slate-500 focus-visible:border-sky-400/70 focus-visible:ring-0"
              />
            </div>
            {msg && <p className="text-sm font-medium text-rose-300">{msg}</p>}
            <Button
              type="submit"
              className="group w-full rounded-2xl bg-white/90 py-4 text-sm font-semibold text-slate-900 transition hover:bg-white"
              disabled={loading}
            >
              {loading ? (
                "Creating account..."
              ) : (
                <>
                  Create account
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-150 group-hover:translate-x-1" />
                </>
              )}
            </Button>
          </form>
          <div className="flex flex-col gap-3 text-xs text-slate-300/90">
            <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
              <span>Encrypted at rest</span>
              <span>Live in under a minute</span>
            </div>
            <p className="text-center text-sm">
              Already onboarded?{" "}
              <Link href="/signin" className="text-white underline-offset-4 hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </SubtleSpotlight>
  )
}

export default function SignUpPage() {
  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-950 text-slate-50">
      <div className="pointer-events-none absolute inset-0">
        <BackgroundGrid className="opacity-15" />
        <div className="absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-sky-900/30 via-transparent to-transparent" />
      </div>
      <div
        className="relative mx-auto grid min-h-screen w-full max-w-6xl items-center gap-12 px-6 py-14 sm:px-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20 lg:px-16"
        style={{
          fontFamily:
            '"Soehne", "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif',
        }}
      >
        <section className="space-y-8">
          <div className="flex flex-wrap gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.35em] text-slate-200/80">
            <span className="rounded-full border border-white/15 bg-white/5 px-4 py-1">Minimal inputs</span>
            <span className="rounded-full border border-white/10 px-4 py-1 text-slate-200/70">Layered surface</span>
            <span className="rounded-full border border-white/10 px-4 py-1 text-slate-200/70">Live payload</span>
          </div>
          <div className="space-y-5">
            <h1 className="text-4xl font-semibold leading-tight text-white sm:text-5xl">
              Minimal signup. Maximum calm.
            </h1>
            <p className="max-w-xl text-base text-slate-300">
              We rebuilt the page so you only see email + password. Everything else stays implied through soft
              surfaces, generous spacing, and lightweight motion.
            </p>
          </div>
          <ul className="grid gap-4 sm:grid-cols-2">
            {signupNotes.map((item) => (
              <li key={item.title} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="text-sm font-semibold text-white">{item.title}</p>
                <p className="mt-1 text-sm text-slate-300">{item.detail}</p>
              </li>
            ))}
          </ul>
        </section>
        <section className="relative flex justify-center">
          <SignUpForm />
        </section>
      </div>
    </main>
  )
}
